import * as React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentType } from '../../../document/constants.ts';
import DocumentAssistance from '../../../document/pages/Assistance';
import { TABS_OPTIONS, useCurrentTabName } from '../../../document/pages/Assistance/tabs.ts';
import ListOfServicesAssistanceHeader, {
    RelevanceSelectionState,
} from '../components/ListOfServicesAssistanceHeader.tsx';
import { ProgressRing } from '../components/ProgressRing.tsx';
import {
    ASSISTANCE_PATH,
    ASSISTANCE_TAB_PATH,
    CHANNEL_FINISHED_PATH,
    CHANNEL_PATH,
    CHANNEL_TESTING_PATH,
    OVERVIEW_FINISHED_PATH,
    OVERVIEW_PATH,
    OVERVIEW_TESTING_PATH,
} from '../constants.ts';
import {
    GET_CHANNELS_OVERVIEW_DATA,
    GET_ASSISTANCE_OVERVIEW_DATA,
    GET_INCOMPLETE_RELEVANT_POSITION_NUMBERS,
    GET_NEXT_ASSISTANCE_RECORD,
    GET_RELATED_DOCUMENT,
    RE_UPLOAD_FILE,
    REOPEN_FOR_ASSISTANCE,
    RETRY_STEP,
    SEND_TO_LABELING,
} from '../queries.ts';
import { useTranslation } from 'react-i18next';
import classnames from '../../../core/utils/classnames.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import PositionAssistanceCard from '../components/PositionAssistanceCard.tsx';
import { useAssistanceContext } from '../../../document/pages/Assistance/AssistanceContext.tsx';
import Button from '../../../core/components/Button.tsx';
import { ArticleNumberFieldConditionalComponents } from '../../../document/components/ArticleNumberField.tsx';
import HeaderDataCard from '../components/HeaderDataCard.tsx';
import { useChapterPrefetching } from '../hooks/useChapterPrefetching';
import { useChapterManagement } from '../hooks/useChapterManagement';
import { usePositionsData } from '../hooks/usePositionsData';
import { ListOfServicesStateProvider, useListOfServicesState } from '../hooks/useListOfServicesState.tsx';
import { FocusPositionProvider, useFocusPosition } from '../hooks/useFocusPosition.tsx';
import { useScrollSync } from '../hooks/useScrollSync';
import {
    CustomerNumberField,
    getCustomerFieldConditionalComponents,
    getCustomerFieldGroupConditionalComponents,
} from '../../../document/components/CustomerField.tsx';
import DOMPurify from 'dompurify';
import {
    faArrowUpRightFromSquare,
    faBarsFilter,
    faChevronDown,
    faChevronUp,
    faEllipsisVertical,
    faList,
    faPlus,
    faSpinnerThird,
    faTableCells,
    faThumbsDown,
    faThumbsUp,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../../core/components/Icon.tsx';
import Loader from '../../../core/components/Loader.tsx';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import SplitPanes, { Pane } from '../../../core/components/SplitPanes.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import Pretext from '../components/Pretext.tsx';
import Chapter from '../components/Chapter.tsx';
import CompleteButton from '../components/CompleteButton.tsx';
import { ChapterNavigation } from '../components/ChapterNavigation.tsx';
import useSearchParamState, {
    useArraySearchParamState,
    useBooleanSearchParamState,
} from '../../../core/utils/useSearchParamState.tsx';
import { useLocalStorage } from '../../../core/utils/hooks/useLocalStorage.ts';
import SourceFileView from '../../../document/pages/Assistance/components/SourceFileView.tsx';
import HighlightText, { countKeywordMatches, useHighlightKeywords } from '../components/HighlightText.tsx';
import { url } from '../../../core/utils/link.ts';
import { filterAttachments } from '../../../document/utils.ts';
import {
    AssistanceSidebarAttachments,
    AssistanceSidebarMasterData,
    AssistanceSidebarProcessingHistory,
} from '../../../document/pages/Assistance/DocumentView.tsx';
import { useNavigate } from 'react-router';
import posthog from 'posthog-js';
import Switch from '../../../core/components/Switch.tsx';
import FocusButton from '../../../document/pages/Assistance/FocusButton.tsx';
import ItemDataSection from '../../../document/pages/Assistance/ItemDataSection.tsx';
import { VirtualizedItemListHandle } from '../../../document/pages/Assistance/VirtualizedItemList.tsx';
import AssistanceSection from '../../../document/pages/Assistance/AssistanceSection.tsx';
import { useAuth } from '../../../core/utils/authentication.ts';
import { isFeatureEnabled } from '../../../customizations/framework.tsx';
import { useDocumentConfig, getFormFieldGroups } from '../../../document/pages/Assistance/useDocumentConfig.ts';
import Suggestions, { ISuggestion } from '../components/Suggestions.tsx';
import { ArticlesContent } from '../components/ArticlesContent.tsx';
import { ArticleFormContextProvider } from '../components/PositionArticleSetCard.tsx';
import AssistanceNotificationsCard from '../../../document/pages/Assistance/components/AssistanceNotificationsCard.tsx';
import { aggregateChapterCounts } from '../utils.ts';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';

const SCROLL_ANCHOR_OFFSET = 24;
const POSITION_SCROLL_ANCHOR_OFFSET = 60; // section header that is sticky is 80px high - 20px padding
const STICKY_FILTER_OFFSET = 72;

/**
 * Scroll a positions scroll container so that the element with the given position index is visible.
 */
const scrollToPositionElement = (
    container: HTMLElement,
    positionIndex: number,
    behavior: ScrollBehavior = 'smooth'
) => {
    const el = container.querySelector(`[data-position-index="${positionIndex}"]`);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    container.scrollTo({
        top: rect.top + container.scrollTop - containerRect.top - STICKY_FILTER_OFFSET,
        behavior,
    });
};

const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);
const LoadingIcon = withIcon(faSpinnerThird);
const ThumbsUpIcon = withIcon(faThumbsUp);
const ThumbsDownIcon = withIcon(faThumbsDown);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);
const MoreIcon = withIcon(faEllipsisVertical);
const AddIcon = withIcon(faPlus);
const RemoveIcon = withIcon(faTrashCan);
const FilterIcon = withIcon(faBarsFilter);
const ListViewIcon = withIcon(faList);
const TableViewIcon = withIcon(faTableCells);

const ARTICLE_FIELD_PREFIX = 'positions__article_sets__articles';
const POSITION_FIELD_PREFIX = 'positions';

export interface IPosition {
    id: string;
    positionIndex: number;
    chapter: string;
    number: IFieldData;
    text: IFieldData;
    isRelevant: IFieldData;
    isIncluded?: boolean | null;
    rejectedBy: IFieldData;
    articleSets: IArticleSet[];
    pageIndex: number;
    prediction: string;
    parent: string;
    [key: string]: any;
}

export interface IChapter {
    number: string;
    pageIndex: number;
    numberTotal: number;
    numberRelevant: number;
    numberCompleted: number;
    isExpanded: boolean;
    containsHighlightKeywords: boolean;
    header: string;
    positions: IPosition[];
    isLoading?: boolean;
}

const noOp = () => {};

export const getScrollAnchors = (container: HTMLElement) => {
    return container.querySelectorAll('[data-scroll-anchor="chapterHeader"], [data-scroll-anchor]');
};

export const getClosestScrollAnchorIndex = (container: HTMLElement) => {
    // relevantNodes is a list of all nodes with data-relevant="true"
    const relevantNodes = getScrollAnchors(container);
    const offsetTop = container.getBoundingClientRect().top;

    let closestChildIndex = null;
    let minDistance = Infinity;

    relevantNodes.forEach((child, index) => {
        const childRect = child.getBoundingClientRect();
        const distance = Math.abs(childRect.top - offsetTop - SCROLL_ANCHOR_OFFSET);

        if (distance <= minDistance) {
            minDistance = distance;
            closestChildIndex = index;
        }
    });

    if (closestChildIndex !== null) {
        // with this if condition we prevent that when we're at the bottom of the list set the index to null
        // but rather keep it at the last index
        return closestChildIndex;
    }
};

export const ListOfServicesPositionsTab = () => {
    const { record, setRecord } = useAssistanceContext();
    if (!record) {
        return <Loader className="p-10" />;
    }
    return <ListOfServicesPositionsTabContent record={record} setRecord={setRecord} />;
};

const SuggestionsWrapper = ({
    suggestions,
    setIndex,
    articleIndex,
    positionIndex,
    indexInChapter,
    shouldShow,
    onArticleAdded,
    virtualItemIndex,
    onEnsureChapterLoaded,
    handleUpdate,
    selectedArticleIds,
}) => {
    if (!suggestions || suggestions.length === 0 || !shouldShow) return null;

    const handleSelect = async (suggestion) => {
        if (onEnsureChapterLoaded && virtualItemIndex !== undefined) {
            onEnsureChapterLoaded({ itemIndex: virtualItemIndex });
        }

        await handleUpdate({
            itemIndex: positionIndex,
            action: 'action:change_article_field',
            payload: {
                setIndex,
                articleIndex,
                indexInChapter,
                _lookupEntity: suggestion,
            },
        });
        onArticleAdded?.({ updateNumbers: false });
    };

    return (
        <div className="mt-4">
            <Suggestions suggestions={suggestions} onSelect={handleSelect} selectedArticleIds={selectedArticleIds} />
        </div>
    );
};

// <--- helpers --->
const isArticleSetEmpty = (articles: any[]) => {
    return articles.every((a) => a.fields.every((field) => !field?.value));
};

const canCompleteArticleSet = (articles: any[]) => {
    return articles.every((a) => a.fields.every((field) => !field.config.isRequired || Boolean(field.value)));
};

const getArticlesInSet = (allArticles: any[], setIndex: number) => {
    return allArticles.filter((a) => a.setIndex === setIndex);
};

export const ItemDataSectionWithDragDrop = ({
    positionGroup,
    articlesViewMode,
    positionsScrollRef,
    onArticleAdded,
    onEnsureChapterLoaded,
    chapterMap,
    onChapterHeaderClick,
    positionExpandFunctionsRef,
    handleUpdate,
    selectedArticleIds,
}: any) => {
    const { t } = useTranslation('assistance');
    const { requestFocus, focusArticles } = useFocusPosition();
    const { handlers, readOnly, loading, record } = useAssistanceContext();
    const { user } = useAuth();
    const [draggedSetIndex, setDraggedSetIndex] = useState<number | null>(null);

    const position = positionGroup.position;
    const hasMultipleSets = positionGroup.articleSets?.length > 1;
    const title = positionGroup.positionText?.split('\n')[0] || '';

    const documentConfig = record?.channel?.documentConfig;
    const { generalConfig, fieldConfigsByPrefix } = useDocumentConfig(documentConfig, [
        ARTICLE_FIELD_PREFIX,
        POSITION_FIELD_PREFIX,
    ]);
    const positionFieldConfigs = fieldConfigsByPrefix?.[POSITION_FIELD_PREFIX] || [];
    const positionFormFieldGroups = useMemo(
        () => getFormFieldGroups(position, positionFieldConfigs, POSITION_FIELD_PREFIX),
        [position, positionFieldConfigs]
    );

    // Check if a customization that adds controls is active (e.g., Trox)
    // These customizations add crosshair + more actions buttons that need header alignment
    const customizationKey = user?.customer?.customizationKey?.toLowerCase() || '';
    const hasCustomizationControls = customizationKey === 'trox';

    const chapterMapRef = useRef(chapterMap);
    useEffect(() => {
        chapterMapRef.current = chapterMap;
    }, [chapterMap]);

    // Ensure record.listOfServicesUnsaved.positions contains the correct chapter's positions
    // before calling handlers.onUpdate
    const syncChapterPositions = useCallback(() => {
        const chapterPositions = chapterMapRef.current?.get(position.chapter)?.positions;
        if (chapterPositions !== undefined && record?.listOfServicesUnsaved) {
            record.listOfServicesUnsaved.positions = chapterPositions;
        }
    }, [record, position.chapter]);

    const handlePositionFieldUpdate = useCallback(
        (fieldName: string, updateEvent: any) => {
            syncChapterPositions();
            return handlers.onUpdate({
                fieldName,
                itemIndex: position.positionIndex,
                action: 'action:change_position_field',
                payload: {
                    ...(updateEvent.payload || {}),
                    indexInChapter: position.indexInChapter,
                },
            });
        },
        [syncChapterPositions, handlers, position.positionIndex, position.indexInChapter]
    );

    // Find which article set a given article index belongs to
    const findArticleSet = useCallback(
        (articleIdx: number) => {
            return positionGroup.articleSets.find((set) => articleIdx >= set.startIndex && articleIdx <= set.endIndex);
        },
        [positionGroup.articleSets]
    );

    // <---- event handlers ---->
    const handleDragMove = useCallback((newIndex: number) => {
        setDraggedSetIndex(newIndex);
    }, []);

    const handleDragDrop = useCallback(
        async (item: { index: number; setIndex: number }) => {
            setDraggedSetIndex(null);
            if (item.setIndex === undefined || draggedSetIndex === null) return;

            syncChapterPositions();
            await handlers.onUpdate({
                itemIndex: position.positionIndex,
                action: 'action:drag_article_set',
                payload: {
                    setIndex: item.setIndex,
                    newDragSetIndex: draggedSetIndex > item.setIndex ? draggedSetIndex - 1 : draggedSetIndex,
                    indexInChapter: position.indexInChapter,
                },
            });
            onArticleAdded?.({ updateNumbers: false });
        },
        [syncChapterPositions, draggedSetIndex, position, handlers, onArticleAdded]
    );

    const handleRemoveArticle = useCallback(
        async (virtualItemIndex: number) => {
            const article = positionGroup.articles.find((a) => a.fields[0]?.itemIndex === virtualItemIndex);
            if (!article) return;

            syncChapterPositions();
            await handlers.onUpdate({
                itemIndex: position.positionIndex,
                action: 'action:remove_article',
                payload: {
                    setIndex: article.setIndex,
                    articleIndex: article.articleIndex,
                    indexInChapter: position.indexInChapter,
                },
            });
            onArticleAdded?.({ updateNumbers: false });
        },
        [syncChapterPositions, position, positionGroup, handlers, onArticleAdded]
    );

    const handleIsCompleteChange = useCallback(
        async (setIndex: number, newIsComplete: boolean) => {
            syncChapterPositions();
            await handlers.onUpdate({
                itemIndex: position.positionIndex,
                action: 'action:change_is_complete',
                payload: {
                    setIndex,
                    setIsComplete: newIsComplete,
                    indexInChapter: position.indexInChapter,
                },
            });
            onArticleAdded?.();
        },
        [syncChapterPositions, position, handlers, onArticleAdded]
    );

    const focusFirstField = (formGroups) => {
        if (formGroups.length === 0) return;

        const lastFormGroup = formGroups[formGroups.length - 1];
        const firstInput = lastFormGroup.querySelector('input, textarea, select');

        if (firstInput) {
            (firstInput as HTMLInputElement).focus();
            (firstInput as HTMLInputElement).select?.();
        }
    };

    const handleAddArticle = useCallback(
        async (setIndex: number) => {
            syncChapterPositions();
            await handlers.onUpdate({
                itemIndex: position.positionIndex,
                action: 'action:add_article',
                payload: {
                    setIndex,
                    indexInChapter: position.indexInChapter,
                },
            });

            await onArticleAdded?.({ updateNumbers: false });

            setTimeout(() => {
                const positionContainer = document.querySelector(
                    `[data-position-container="${position.positionIndex}"]`
                );
                if (!positionContainer) {
                    return;
                }

                const formGroups = positionContainer.querySelectorAll(
                    `.js-form-item-group[data-set-index="${setIndex}"]`
                );
                focusFirstField(formGroups);
            }, 100);
        },
        [syncChapterPositions, position, handlers, onArticleAdded]
    );

    const handleAddAlternativePosition = useCallback(async () => {
        syncChapterPositions();
        await handlers.onUpdate({
            itemIndex: position.positionIndex,
            action: 'action:add_article_set',
            payload: {
                indexInChapter: position.indexInChapter,
            },
        });

        await onArticleAdded?.({ updateNumbers: false });

        setTimeout(() => {
            const positionContainer = document.querySelector(`[data-position-container="${position.positionIndex}"]`);
            if (!positionContainer) {
                return;
            }

            const formGroups = positionContainer.querySelectorAll('.js-form-item-group');

            focusFirstField(formGroups);
        }, 100);
    }, [syncChapterPositions, position, handlers, onArticleAdded]);

    // <---- render helpers ---->
    const renderCompleteButton = (setIndex: number) => {
        const articleSet = positionGroup.articleSets.find((set) => set.setIndex === setIndex);
        if (!articleSet) return null;

        const articlesInSet = getArticlesInSet(positionGroup.articles, setIndex);
        if (isArticleSetEmpty(articlesInSet)) return null;

        const canComplete = canCompleteArticleSet(articlesInSet);

        return (
            <CompleteButton
                isComplete={articleSet.isComplete}
                disabled={!canComplete}
                onIsCompleteChange={(newIsComplete) => handleIsCompleteChange(setIndex, newIsComplete)}
            />
        );
    };

    const renderRemoveButton = (virtualItemIndex: number) => {
        const article = positionGroup.articles.find((a) => a.fields[0]?.itemIndex === virtualItemIndex);
        if (!article) return null;

        const articleSet = positionGroup.articleSets.find((set) => set.setIndex === article.setIndex);
        if (!articleSet) return null;

        const isAlternativeSet = article.setIndex > 0;
        const hasMultipleArticles = articleSet.endIndex - articleSet.startIndex + 1 > 1;
        const canRemove = isAlternativeSet || hasMultipleArticles;

        if (!canRemove) return null;

        return (
            <AssistanceSection.FieldGroupIconButton
                disabled={readOnly || loading}
                onClick={() => handleRemoveArticle(virtualItemIndex)}
                className="hover:text-content-danger-strong hover:bg-surface-danger-weak"
            >
                <RemoveIcon />
            </AssistanceSection.FieldGroupIconButton>
        );
    };

    const renderAddArticleButton = (articleIdx: number) => {
        const articleSet = findArticleSet(articleIdx);
        const isLastInSet = articleSet && articleIdx === articleSet.endIndex;
        if (!isLastInSet) return null;

        return (
            <Tooltip content={t('listOfServices.articleSetCard.addArticle')} side="top">
                <AssistanceSection.FieldGroupIconButton
                    disabled={readOnly || loading}
                    onClick={() => handleAddArticle(articleSet.setIndex)}
                >
                    <AddIcon />
                </AssistanceSection.FieldGroupIconButton>
            </Tooltip>
        );
    };

    const renderSuggestions = (localArticleIdx: number) => {
        const article = positionGroup.articles[localArticleIdx];
        if (!article) return null;

        const articleSet = findArticleSet(localArticleIdx);
        if (!articleSet) return null;

        const articleFields = article.fields;

        // Check if article number and description are empty for current article
        const numberField = articleFields.find((field) => field.fieldName === 'number');
        const descriptionField = articleFields.find((field) => field.fieldName === 'description');
        const orderCodeField = articleFields.find((field) => field.fieldName === 'order_code');
        const shouldShow = !numberField?.value && !descriptionField?.value && !orderCodeField?.value;

        return (
            <SuggestionsWrapper
                suggestions={article.suggestions}
                setIndex={articleSet.setIndex}
                articleIndex={article.articleIndex}
                positionIndex={position.positionIndex}
                indexInChapter={position.indexInChapter}
                shouldShow={shouldShow}
                onArticleAdded={onArticleAdded}
                virtualItemIndex={articleFields[0]?.itemIndex}
                onEnsureChapterLoaded={onEnsureChapterLoaded}
                handleUpdate={handleUpdate}
                selectedArticleIds={selectedArticleIds}
            />
        );
    };

    const handleFocusPosition = useCallback(async () => {
        const chapterNumber = positionGroup.position.chapter;

        // Signal which position to focus — the position container's effect will handle expand + scroll
        requestFocus(positionGroup.positionIndex);

        // Always request the chapter to expand — the handler is idempotent if already open.
        // We avoid reading chapterMap here because it may be stale in the closure.
        if (onChapterHeaderClick) {
            await onChapterHeaderClick({
                openChapter: true,
                chapter: chapterNumber,
            });
        }

        // Align the clicked article set to the top of this pane. Targets the clicked position
        // by index, so it never jumps to a different article set; at the end of the list the
        // scroll just clamps.
        focusArticles(positionGroup.positionIndex);
    }, [
        positionGroup.positionIndex,
        positionGroup.position.chapter,
        requestFocus,
        focusArticles,
        onChapterHeaderClick,
    ]);

    return (
        <div data-position-container={positionGroup.positionIndex}>
            <ArticleFormContextProvider
                fields={positionGroup.articles.flatMap((a) => a.fields)}
                article={positionGroup.articles[0]?.article}
            >
                <ItemDataSection
                    itemType="articles"
                    formFieldGroups={positionGroup.articles.map((a) => a.fields)}
                    viewMode={articlesViewMode}
                    hideViewModeSwitcher={true}
                    hideAddButton={true}
                    enableDragDrop={hasMultipleSets}
                    draggedSetIndex={draggedSetIndex}
                    customComplete={() => renderCompleteButton(0)}
                    customSeparatorControls={renderCompleteButton}
                    customRowControls={renderRemoveButton}
                    customControlsForLastRow={renderAddArticleButton}
                    customAfterForm={renderSuggestions}
                    onDragMove={handleDragMove}
                    onDragDrop={handleDragDrop}
                    onAddAlternativePosition={handleAddAlternativePosition}
                    customTitle={{
                        positionNumber: positionGroup.positionNumber,
                        positionTitle: title,
                        positionIndex: positionGroup.positionIndex,
                        pageIndex: positionGroup.pageIndex,
                        chapter: position.chapter,
                        onFocus: handleFocusPosition,
                    }}
                    positionFields={positionFormFieldGroups}
                    onPositionFieldUpdate={handlePositionFieldUpdate}
                    generalConfig={generalConfig}
                />
            </ArticleFormContextProvider>
        </div>
    );
};

// Helper function to check if positions match the current filter
const positionsMatchFilter = (positions: IPosition[], relevanceSelection: RelevanceSelectionState[]) => {
    const isRelevantOpen = (position: IPosition) =>
        position.isRelevant.value === 'True' && !position.articleSets.every((articleSet) => articleSet.isComplete);
    const isRelevantCompleted = (position: IPosition) =>
        position.isRelevant.value === 'True' && position.articleSets.every((articleSet) => articleSet.isComplete);
    const isIrrelevantOpen = (position: IPosition) =>
        position.isRelevant.value === 'False' && position.feedbackRequest === 'need_feedback';
    const isIrrelevantCompleted = (position: IPosition) =>
        position.isRelevant.value === 'False' && position.feedbackRequest === 'no_feedback_needed';

    return positions.some((position) => {
        // Pretexts don't count as matching positions
        if (position.prediction === 'PreText') return false;

        if (isRelevantOpen(position) && relevanceSelection.includes(RelevanceSelectionState.RELEVANT_OPEN)) return true;
        if (isRelevantCompleted(position) && relevanceSelection.includes(RelevanceSelectionState.RELEVANT_COMPLETED))
            return true;
        if (isIrrelevantOpen(position) && relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_OPEN))
            return true;
        if (
            isIrrelevantCompleted(position) &&
            relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_COMPLETED)
        )
            return true;
        return false;
    });
};

export const ListOfServicesPositionsTabContent = ({ record, setRecord }) => {
    const { refetchDataNetworkStatus, readOnly, handlers, loading: assistanceLoading } = useAssistanceContext();
    const { chapterMap, setChapterMap, loadedChapters, isChapterLoaded, markChapterAsLoaded, resetLoadedChapters } =
        useListOfServicesState();
    const {
        mode,
        relevanceSelection,
        setRelevanceSelection,
        scrollAnchorIndex,
        setScrollAnchorIndex,
        applyToChapters,
        setApplyToChapters,
        showOnlyIncomplete,
        setShowOnlyIncomplete,
        articlesViewMode,
    } = useListOfServicesAssistanceContext();
    const positionExpandFunctionsRef = useRef<Map<number, () => void>>(new Map());
    const shouldApplyScrollRef = useRef(false);

    const onScrollAnchorIndexChange = useCallback(
        (index: number, source?: 'positions' | 'articles') => {
            if (source === 'positions') {
                setScrollAnchorIndex(index);
            }
        },
        [setScrollAnchorIndex]
    );

    const attachments = useMemo(() => filterAttachments(record), [record?.id]);
    const formsRef = useRef(null);
    const isRefetchingRef = useRef(refetchDataNetworkStatus === NetworkStatus.refetch);
    const [isPositionsSectionExpanded, setIsPositionsSectionExpanded] = useState(true);
    const [isNavCollapsed, setIsNavCollapsed] = useState(() => localStorage.getItem('lv-nav-collapsed') === 'true');
    const [isNavAnimating, setIsNavAnimating] = useState(false);
    const handleToggleNav = () => {
        setIsNavAnimating(true);
        setIsNavCollapsed((v) => {
            const next = !v;
            localStorage.setItem('lv-nav-collapsed', next.toString());
            return next;
        });
        setTimeout(() => setIsNavAnimating(false), 300);
    };

    const [autoScrollEnabled, setAutoScrollEnabled] = useLocalStorage('listOfServices.autoScroll', true);

    const {
        chapter,
        setChapter,
        loading,
        chapterBeingLoaded,
        isChapterActionLoading,
        handleChapterHeaderClick: onChapterHeaderClick,
        handleMarkAllRelevant,
        handleMarkAllNotRelevant,
        loadChapterAndSetPositionsOnRecord,
        totals,
    } = useChapterManagement({
        record,
        setRecord,
        chapterMap,
        setChapterMap,
    });

    const isPending = record.status == 'PENDING' && !record.assistedAt;

    const {
        positionsScrollRef,
        articlesScrollRef,
        debouncedHandleScrollPositions,
        debouncedHandleScrollArticles,
        getScrollAnchors,
        scrollArticlesToPosition,
        scrollToPositionOverrideRef,
        suppressAutoSync,
        setProgrammaticScroll,
    } = useScrollSync(onScrollAnchorIndexChange, autoScrollEnabled);

    const focusArticles = useCallback(
        (positionIndex: number) => {
            scrollArticlesToPosition(String(positionIndex));
        },
        [scrollArticlesToPosition]
    );

    const handleAutoScrollChange = (enabled: boolean) => {
        setAutoScrollEnabled(enabled);
        posthog.capture('lv_autoscroll_toggled', { enabled });
    };

    const positionVirtualizerRef = useRef<VirtualizedItemListHandle>(null);

    const { fieldConfigsByPrefix } = useDocumentConfig(record?.channel?.documentConfig, [ARTICLE_FIELD_PREFIX]);
    const articleFieldConfigs = fieldConfigsByPrefix?.[ARTICLE_FIELD_PREFIX] || [];

    const { articlesByPosition } = usePositionsData(chapterMap, chapter, articleFieldConfigs);

    const highlightSelectedInDocumentSuggestions = isFeatureEnabled('highlightSelectedInDocumentSuggestions');

    const selectedArticleIds = useMemo(() => {
        if (!highlightSelectedInDocumentSuggestions) return undefined;
        const selectedArticleIdSet = new Set<string>();
        articlesByPosition.forEach((group) => {
            group.articles.forEach((article) => {
                const numberField = article.fields.find((f) => f.fieldName === 'number');
                if (numberField?.value) selectedArticleIdSet.add(numberField.value);
            });
        });
        return selectedArticleIdSet;
    }, [articlesByPosition, highlightSelectedInDocumentSuggestions]);

    const filteredArticlesByPosition = useMemo(() => {
        if (!showOnlyIncomplete || readOnly) return articlesByPosition;

        return articlesByPosition.filter((group) => {
            const position = group.position;
            if (!position.articleSets || position.articleSets.length === 0) return false;
            const isIncomplete = !position.articleSets.every((articleSet) => articleSet.isComplete);
            return isIncomplete;
        });
    }, [articlesByPosition, showOnlyIncomplete, readOnly]);

    const filteredArticlesByPositionRef = useRef(filteredArticlesByPosition);
    filteredArticlesByPositionRef.current = filteredArticlesByPosition;

    // Wire up position-level virtualizer scroll for scroll sync
    useEffect(() => {
        let retryTimerId: number | null = null;

        scrollToPositionOverrideRef.current = (positionIndex: string) => {
            const articlesContainer = articlesScrollRef.current;
            if (!articlesContainer) return;

            if (retryTimerId !== null) {
                window.clearTimeout(retryTimerId);
                retryTimerId = null;
            }

            let resolvedIndex = filteredArticlesByPosition.findIndex(
                (g) => String(g.positionIndex) === String(positionIndex)
            );
            let resolvedPositionIndex = positionIndex;

            if (resolvedIndex < 0) {
                const firstNestedIndex = positionsScrollRef.current
                    ?.querySelector(`[data-position-index="${positionIndex}"]`)
                    ?.querySelector('[data-position-index]')
                    ?.getAttribute('data-position-index');
                if (firstNestedIndex) {
                    resolvedIndex = filteredArticlesByPosition.findIndex(
                        (g) => String(g.positionIndex) === firstNestedIndex
                    );
                    if (resolvedIndex >= 0) resolvedPositionIndex = firstNestedIndex;
                }
            }

            const scrollToElementDOM = (): boolean => {
                const el = articlesContainer.querySelector(`[data-position-container="${resolvedPositionIndex}"]`);
                if (!el) return false;
                const rect = el.getBoundingClientRect();
                const containerRect = articlesContainer.getBoundingClientRect();
                setProgrammaticScroll(true);
                articlesContainer.scrollTo({
                    top: rect.top - containerRect.top + articlesContainer.scrollTop - 120,
                    behavior: 'smooth',
                });
                return true;
            };

            if (scrollToElementDOM()) return;

            if (!positionVirtualizerRef.current || resolvedIndex < 0) return;

            const retry = (attemptsLeft: number) => {
                // Re-resolve the numeric index from the stable string identifier on each retry,
                // so that data changes during the retry window don't cause scrolling to wrong positions.
                const currentIndex = filteredArticlesByPositionRef.current.findIndex(
                    (g) => String(g.positionIndex) === String(resolvedPositionIndex)
                );
                const targetIndex = currentIndex >= 0 ? currentIndex : resolvedIndex;

                setProgrammaticScroll(true);
                positionVirtualizerRef.current?.scrollToIndex(targetIndex, { align: 'start' });
                retryTimerId = window.setTimeout(() => {
                    retryTimerId = null;
                    if (!scrollToElementDOM() && attemptsLeft > 1) {
                        retry(attemptsLeft - 1);
                    }
                }, 200);
            };

            retry(6);
        };

        return () => {
            if (retryTimerId !== null) {
                window.clearTimeout(retryTimerId);
            }
            scrollToPositionOverrideRef.current = null;
        };
    }, [filteredArticlesByPosition, scrollToPositionOverrideRef, articlesScrollRef, setProgrammaticScroll]);

    // we can only find the itemIndex on the fields - not sure why it's not directly on the article
    const chapterByArticleIndex = useMemo(() => {
        return Object.fromEntries(
            articlesByPosition.flatMap((group) =>
                group.articles.map((article) => [article.fields[0]?.itemIndex, group.position.chapter])
            )
        );
    }, [articlesByPosition]);

    const document = record?.listOfServicesUnsaved;
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useSearchParamState('mode');

    useChapterPrefetching({
        recordId: record.id,
        chapterMap,
        setChapterMap,
        loadedChapters,
        markChapterAsLoaded,
    });

    const allChaptersLoaded = record.listOfServicesUnsaved
        ? record.listOfServicesUnsaved.numberOfPositions + record.listOfServicesUnsaved.numberOfPretexts ===
          (record.listOfServicesUnsaved.positions || []).length
        : false;

    useEffect(() => {
        shouldApplyScrollRef.current = true;
    }, [mode, relevanceSelection]);

    useEffect(() => {
        if (refetchDataNetworkStatus === NetworkStatus.refetch) {
            isRefetchingRef.current = true;
        }
    }, [refetchDataNetworkStatus]);

    useEffect(() => {
        // Set the chapter map with positions on initial load and on refetch
        // Don't rebuild if we already have a chapterMap and we're not refetching
        if (
            document &&
            (!chapterMap || (isRefetchingRef.current === true && refetchDataNetworkStatus !== NetworkStatus.refetch))
        ) {
            // add chapters with their meta data to a map, keeping the chapters open that were expanded before a refetch
            const initialChapterMap: Map<string, IChapter> = new Map(
                document.chapters.map((chapter) => [
                    chapter.number,
                    { ...chapter, positions: [], isExpanded: chapterMap?.get(chapter.number)?.isExpanded ?? false },
                ])
            );

            if (isRefetchingRef.current) {
                // the refetched record does not contain positions, resetting re-triggers the chapter prefetch
                resetLoadedChapters();
            }

            // add positions to the map
            (document.positions || []).forEach((position) => {
                initialChapterMap.get(position.chapter).positions.push(position);
            });

            // Only expand chapters if explicitly set in URL, otherwise keep all collapsed
            if (chapter && initialChapterMap.has(chapter)) {
                initialChapterMap.get(chapter).isExpanded = true;
            }

            // Mark initially loaded chapters as loaded
            const initiallyLoadedChapters = new Set<string>(
                (document.positions || []).map((position) => position.chapter as string)
            );
            initiallyLoadedChapters.forEach((chapterNumber) => markChapterAsLoaded(chapterNumber));

            setChapterMap(initialChapterMap);

            if (chapter && initialChapterMap.has(chapter)) {
                setTimeout(() => {
                    // directly load chapter if set in url
                    loadChapterAndSetPositionsOnRecord(chapter, initialChapterMap);
                }, 0);
            }
            isRefetchingRef.current = false;
        }
    }, [record, chapter, chapterMap, loadChapterAndSetPositionsOnRecord, markChapterAsLoaded, resetLoadedChapters]);

    const { t } = useTranslation('assistance');

    const [skipPositionsModalVisible, setSkipPositionsModalVisible] = useState(false);
    const [incompleteRelevantPositionNumbers, setIncompleteRelevantPositionNumbers] = useState<string[]>([]);

    const { publishToast } = useToaster();

    // The chapterMap only contains lazily loaded positions, so the check for incomplete
    // positions must be done by the backend across all chapters.
    const [getIncompleteRelevantPositionNumbers, { loading: checkingIncompletePositions }] = useLazyQuery(
        GET_INCOMPLETE_RELEVANT_POSITION_NUMBERS,
        {
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: false,
        }
    );

    const handleFinishClick = useCallback(async () => {
        let numbers: string[] | undefined;
        try {
            const { data, error } = await getIncompleteRelevantPositionNumbers({ variables: { id: record.id } });
            numbers = data?.listOfServicesProcessingRecord?.incompleteRelevantPositionNumbers;
            if (error || numbers === undefined) {
                throw error ?? new Error('Missing incompleteRelevantPositionNumbers in response');
            }
        } catch {
            // Fail-safe: never finish when the incomplete-position check itself failed
            publishToast({ description: t('finishButton.checkIncompleteError'), status: 'error' });
            return;
        }

        if (numbers.length > 0) {
            setIncompleteRelevantPositionNumbers(numbers);
            setSkipPositionsModalVisible(true);
        } else {
            // returning the promise keeps the finish button in its loading state
            // until the mutation, refetch and navigation have completed
            return handlers.onFinish();
        }
    }, [getIncompleteRelevantPositionNumbers, record?.id, handlers, publishToast, t]);

    // Collapse chapters when filters change and they no longer have matching positions
    useEffect(() => {
        if (!applyToChapters) return;

        setChapterMap((prevMap) => {
            if (!prevMap) return prevMap;

            const updatedMap = new Map(prevMap);
            let hasChanges = false;

            updatedMap.forEach((chapterData, chapterNumber) => {
                const hasMatchingPositions = positionsMatchFilter(chapterData.positions, relevanceSelection);

                // Collapse chapters that don't have matching positions
                if (!hasMatchingPositions && chapterData.isExpanded) {
                    chapterData.isExpanded = false;
                    hasChanges = true;
                }
            });

            return hasChanges ? updatedMap : prevMap;
        });
    }, [relevanceSelection, applyToChapters]);

    const filteredChapters = chapterMap
        ? Array.from(chapterMap).filter(([chapterNumber, chapterData]) => {
              if (!applyToChapters) return true;

              if (!isChapterLoaded(chapterNumber)) {
                  const showRelevant =
                      relevanceSelection.includes(RelevanceSelectionState.RELEVANT_OPEN) ||
                      relevanceSelection.includes(RelevanceSelectionState.RELEVANT_COMPLETED);
                  const showIrrelevant =
                      relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_OPEN) ||
                      relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_COMPLETED);
                  return (
                      (showRelevant && chapterData.numberRelevant > 0) ||
                      (showIrrelevant && chapterData.numberTotal - chapterData.numberRelevant > 0)
                  );
              }
              return positionsMatchFilter(chapterData.positions, relevanceSelection);
          })
        : [];

    const handleChapterHeaderClick = useCallback(
        async ({ openChapter, chapter: clickedChapter }) => {
            await onChapterHeaderClick({ openChapter, chapter: clickedChapter });

            const scrollAnchors = getScrollAnchors(articlesScrollRef.current)?.filter(
                (anchor) => anchor.attributes.getNamedItem('data-scroll-anchor')?.value === 'assistanceSection'
            );

            const firstAnchorPerChapter = {};
            scrollAnchors.forEach((anchor) => {
                const chapter = anchor.querySelector('h4')?.textContent?.split('.')[0];
                if (chapter && !(chapter in firstAnchorPerChapter)) {
                    firstAnchorPerChapter[chapter] = anchor;
                }
            });

            const targetAnchor = firstAnchorPerChapter[clickedChapter];
            if (targetAnchor) {
                articlesScrollRef.current.scrollTo({
                    top:
                        targetAnchor.getBoundingClientRect().top -
                        articlesScrollRef.current.getBoundingClientRect().top +
                        articlesScrollRef.current.scrollTop -
                        POSITION_SCROLL_ANCHOR_OFFSET,
                    behavior: 'smooth',
                });
            }
        },
        [onChapterHeaderClick, chapterMap]
    );

    const [selectedPositionIndex, setSelectedPositionIndex] = useState<number | null>(null);
    const pendingScrollPositionIndexRef = useRef<number | null>(null);

    const scrollToPositionIndex = useCallback(
        (positionIndex: number) => {
            suppressAutoSync(2000);
            if (positionsScrollRef.current) {
                scrollToPositionElement(positionsScrollRef.current, positionIndex, 'instant');
            }
            scrollArticlesToPosition(String(positionIndex));
        },
        [positionsScrollRef, scrollArticlesToPosition, suppressAutoSync]
    );

    // scroll after chapterMap updates when the chapter had to be fetched
    useEffect(() => {
        const pi = pendingScrollPositionIndexRef.current;
        if (pi === null) return;
        const el = positionsScrollRef.current?.querySelector(`[data-position-index="${pi}"]`);
        if (!el) return;
        pendingScrollPositionIndexRef.current = null;
        scrollToPositionIndex(pi);
    }, [chapterMap, scrollToPositionIndex]);

    const handleNavPositionClick = useCallback(
        async (positionIndex: number, chapterKey: string) => {
            setSelectedPositionIndex(positionIndex);

            if (chapter === chapterKey) {
                scrollToPositionIndex(positionIndex);
            } else {
                pendingScrollPositionIndexRef.current = positionIndex;
                await handleChapterHeaderClick({ openChapter: true, chapter: chapterKey });
                requestAnimationFrame(() => {
                    const pi = pendingScrollPositionIndexRef.current;
                    if (pi === null) return;
                    const el = positionsScrollRef.current?.querySelector(`[data-position-index="${pi}"]`);
                    if (!el) return;
                    pendingScrollPositionIndexRef.current = null;
                    scrollToPositionIndex(pi);
                });
            }
        },
        [chapter, handleChapterHeaderClick, scrollToPositionIndex]
    );

    const ensureChapterLoadedForItem = async (e) => {
        // we can only do updates to the loaded chapter
        const focusedChapter = chapterByArticleIndex[e?.itemIndex?.toString()];

        if (!e || chapter === focusedChapter) return;

        if (focusedChapter) {
            setChapter(focusedChapter);
            await loadChapterAndSetPositionsOnRecord(focusedChapter, chapterMap);

            const updatedChapterMap = new Map(chapterMap);
            chapterMap.forEach((chapterData, key) => {
                updatedChapterMap.set(key, {
                    ...chapterData,
                    isExpanded: key === focusedChapter,
                });
            });
            setChapterMap(updatedChapterMap);
        }
    };

    const { setArticlesViewMode } = useListOfServicesAssistanceContext();

    const filterCounts = aggregateChapterCounts(record?.listOfServicesUnsaved?.chapters || []);

    // calculate if showing only relevant positions
    const isShowingOnlyRelevant =
        relevanceSelection.length === 2 &&
        relevanceSelection.includes(RelevanceSelectionState.RELEVANT_OPEN) &&
        relevanceSelection.includes(RelevanceSelectionState.RELEVANT_COMPLETED);

    const setRelevantFilter = (onlyRelevant: boolean) => {
        if (onlyRelevant) {
            setRelevanceSelection([RelevanceSelectionState.RELEVANT_OPEN, RelevanceSelectionState.RELEVANT_COMPLETED]);
            setApplyToChapters(true);
        } else {
            setRelevanceSelection([
                RelevanceSelectionState.RELEVANT_OPEN,
                RelevanceSelectionState.RELEVANT_COMPLETED,
                RelevanceSelectionState.IRRELEVANT_OPEN,
                RelevanceSelectionState.IRRELEVANT_COMPLETED,
            ]);
        }
    };

    const {
        documentConfiguration,
        handlers: { onRetryStep },
    } = useAssistanceContext();

    const currentTab = useCurrentTabName();

    const closeUrl = url(
        ASSISTANCE_TAB_PATH,
        { recordId: record.id, tab: 'document' },
        { keepSearch: true, excludeSearch: ['mode'] }
    );

    const isSidebarOpen =
        viewMode === 'attachments' ||
        currentTab === TABS_OPTIONS.MasterData ||
        currentTab === TABS_OPTIONS.ProcessingHistory;

    const sidebarContent =
        viewMode === 'attachments' ? (
            <AssistanceSidebarAttachments attachments={attachments} onClose={() => navigate(closeUrl)} />
        ) : currentTab === TABS_OPTIONS.MasterData ? (
            <AssistanceSidebarMasterData
                record={record}
                documentConfig={documentConfiguration}
                handlers={{ onRetryStep }}
                onClose={() => navigate(closeUrl)}
            />
        ) : currentTab === TABS_OPTIONS.ProcessingHistory ? (
            <AssistanceSidebarProcessingHistory
                record={record}
                documentConfig={documentConfiguration}
                handlers={{ onRetryStep }}
                onClose={() => navigate(closeUrl)}
            />
        ) : null;

    const main = (
        <SplitPanes id="list-of-services-nav" initialWidths={[22, 78]}>
            <Pane
                className={`${isNavAnimating ? 'transition-[width] duration-300 ease-in-out' : ''} ${isNavCollapsed ? '!w-[4rem] !min-w-0 !overflow-hidden' : ''}`}
                hideDivider={isNavCollapsed}
            >
                <ChapterNavigation
                    chapterMap={chapterMap ? new Map(filteredChapters) : null}
                    currentChapter={chapter}
                    selectedPositionIndex={selectedPositionIndex ?? undefined}
                    totals={totals}
                    onChapterClick={(chapterNum) => {
                        setSelectedPositionIndex(null);
                        handleChapterHeaderClick({ openChapter: true, chapter: chapterNum });
                    }}
                    onAutoExpand={async (chapterKey) => {
                        await onChapterHeaderClick({ openChapter: true, chapter: chapterKey });
                        setChapter('');
                    }}
                    onPositionClick={handleNavPositionClick}
                    onOverviewClick={() => {
                        onChapterHeaderClick({ openChapter: false, chapter: '' });
                        articlesScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    isCollapsed={isNavCollapsed}
                    onToggleCollapsed={handleToggleNav}
                />
            </Pane>
            <Pane
                className={`${isNavAnimating ? 'transition-[width] duration-300 ease-in-out' : ''}${isNavCollapsed ? ' flex-1' : ''}`}
            >
                <SplitPanes id="list-of-services-main" initialWidths={[45, 55]}>
                    <Pane>
                        <div
                            className="flex-col w-full h-full bg-surface-white overflow-auto pb-10"
                            ref={positionsScrollRef}
                            onScroll={debouncedHandleScrollPositions}
                        >
                            <div className="flex justify-between items-center sticky top-0 z-50 bg-surface-white p-4">
                                <div className="flex items-center shadow-control" role="group">
                                    <Tooltip className="text-center" content={t('listOfServicesView.tooltip.all')}>
                                        <button
                                            type="button"
                                            onClick={() => setRelevantFilter(false)}
                                            className={classnames(
                                                'flex items-center h-8 px-3 py-1.5 rounded-l text-sm font-medium transition-colors cursor-pointer border border-solid',
                                                !isShowingOnlyRelevant
                                                    ? 'bg-surface-brand-weak text-content-brand-strong border-line-brand-default'
                                                    : 'bg-surface-white text-content-strong border-line-strong hover:bg-surface-weak'
                                            )}
                                        >
                                            {t('listOfServicesView.filters.all')}
                                        </button>
                                    </Tooltip>
                                    <Tooltip className="text-center" content={t('listOfServicesView.tooltip.relevant')}>
                                        <button
                                            type="button"
                                            onClick={() => setRelevantFilter(true)}
                                            className={classnames(
                                                'flex items-center h-8 px-3 py-1.5 rounded-r text-sm font-medium transition-colors cursor-pointer border-y border-r border-solid',
                                                isShowingOnlyRelevant
                                                    ? 'bg-surface-brand-weak text-content-brand-strong border-line-brand-default'
                                                    : 'bg-surface-white text-content-strong border-line-strong hover:bg-surface-weak'
                                            )}
                                        >
                                            {t('listOfServicesView.filters.relevant')}
                                        </button>
                                    </Tooltip>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <Switch value={autoScrollEnabled} onValueChange={handleAutoScrollChange} />
                                    <span className="text-sm font-medium text-content-strong">
                                        {t('listOfServicesView.autoScroll')}
                                    </span>
                                </label>
                            </div>
                            <Chapter>
                                <Chapter.Content>
                                    {chapterMap && filteredChapters.length > 0 ? (
                                        filteredChapters.map(([chapterNumber, chapterData], i) => {
                                            return (
                                                <ListOfServicesChapter
                                                    key={i}
                                                    relevanceSelection={relevanceSelection}
                                                    chapter={chapterNumber}
                                                    onChapterHeaderClick={handleChapterHeaderClick}
                                                    onMarkAllRelevantClick={handleMarkAllRelevant}
                                                    onMarkAllNotRelevantClick={handleMarkAllNotRelevant}
                                                    isChapterActionLoading={isChapterActionLoading}
                                                    loadingChapter={loading && chapterBeingLoaded === chapterNumber}
                                                    allChaptersLoaded={allChaptersLoaded}
                                                    onPositionRelevantChange={noOp}
                                                    onArticleSetIsCompleteChange={noOp}
                                                    onPositionIncludedChange={noOp}
                                                    isLoading={chapterData.isLoading}
                                                    applyToChapters={applyToChapters}
                                                    positionExpandFunctionsRef={positionExpandFunctionsRef}
                                                    chapterIndex={i}
                                                    isLastChapter={i === filteredChapters.length - 1}
                                                    nextChapterKey={filteredChapters[i + 1]?.[0] ?? null}
                                                    positionsScrollRef={positionsScrollRef}
                                                    {...chapterData}
                                                />
                                            );
                                        })
                                    ) : chapterMap && applyToChapters && filteredChapters.length === 0 ? (
                                        <NoChaptersCard />
                                    ) : isPending ? (
                                        <div className="flex items-center justify-center w-full h-full min-h-100">
                                            <AssistanceNotificationsCard
                                                record={record}
                                                documentType="list_of_services"
                                            />
                                        </div>
                                    ) : null}
                                </Chapter.Content>
                            </Chapter>
                        </div>
                    </Pane>
                    <Pane className="flex flex-col overflow-hidden">
                        <div className="absolute top-0 right-0 w-[calc(34%+90px)] h-full bg-surface-transparent pointer-events-none"></div>
                        <div
                            className="flex-col w-full flex-auto bg-surface-white overflow-auto"
                            ref={articlesScrollRef}
                            onScroll={debouncedHandleScrollArticles}
                        >
                            {document && <ListOfServicesHeaderDataContainer />}
                            <Chapter
                                data-scroll-anchor="chapterHeader"
                                expanded={isPositionsSectionExpanded}
                                onExpandedChange={setIsPositionsSectionExpanded}
                            >
                                {chapterMap && (
                                    <>
                                        <Chapter.Header
                                            isMain
                                            isSectionTitle
                                            className={classnames(
                                                'cursor-pointer !my-0 p-6 bg-surface-white border-t border-l border-t-line-weak border-l-line-weak border-r-0 !mb-0',
                                                !isPositionsSectionExpanded ? 'border-b border-b-line-weak' : ''
                                            )}
                                            controls={
                                                <ChapterControls
                                                    numberCompleted={totals.numberRelevantCompleted}
                                                    numberRelevant={totals.numberRelevant}
                                                    numberTotal={totals.numberTotal}
                                                    isChapterActionLoading={isChapterActionLoading}
                                                    onMarkAllNotRelevantClick={null}
                                                    onMarkAllRelevantClick={null}
                                                    articlesViewMode={articlesViewMode}
                                                    onArticlesViewModeChange={setArticlesViewMode}
                                                />
                                            }
                                        >
                                            <h3 className="font-semibold text-sm/7">
                                                {t('listOfServicesView.positionsHeader')}
                                            </h3>
                                        </Chapter.Header>
                                        <Chapter.Content>
                                            <ArticlesContent
                                                articlesByPosition={filteredArticlesByPosition}
                                                loading={loading}
                                                chapterBeingLoaded={chapterBeingLoaded}
                                                chapter={chapter}
                                                articlesViewMode={articlesViewMode}
                                                positionsScrollRef={positionsScrollRef}
                                                formsRef={formsRef}
                                                articlesScrollRef={articlesScrollRef}
                                                onArticleAdded={noOp}
                                                onEnsureChapterLoaded={(e) => {
                                                    void ensureChapterLoadedForItem(e);
                                                }}
                                                chapterMap={chapterMap}
                                                // Deliberately the raw handler: it only opens/loads the chapter.
                                                // The wrapped handleChapterHeaderClick would also scroll this
                                                // pane to the chapter start, which is wrong for the Focus button.
                                                onChapterHeaderClick={onChapterHeaderClick}
                                                positionExpandFunctionsRef={positionExpandFunctionsRef}
                                                positionVirtualizerRef={positionVirtualizerRef}
                                                selectedArticleIds={selectedArticleIds}
                                            />
                                        </Chapter.Content>
                                    </>
                                )}
                            </Chapter>
                        </div>
                        {!readOnly && (
                            <div className="flex flex-col gap-4 flex-none overflow-hidden p-6 border-t border-solid border-line-weak field-modal-root bg-surface-white">
                                <div className="flex gap-2 items-center">
                                    <Tooltip
                                        className="text-center"
                                        content={t(
                                            `listOfServicesView.tooltip.${showOnlyIncomplete ? 'all' : 'incomplete'}`
                                        )}
                                    >
                                        <Button
                                            onClick={() => setShowOnlyIncomplete(!showOnlyIncomplete)}
                                            className={classnames(
                                                'flex gap-2 items-center bg-surface-weak text-content-strong shadow-sm rounded px-3 py-1.5 font-medium text-sm border border-solid border-line-strong outline-none',
                                                'hover:bg-surface-strong transition-colors',
                                                showOnlyIncomplete &&
                                                    'bg-surface-brand-medium-default text-content-brand-strong border-line-brand-default hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                                            )}
                                        >
                                            <FilterIcon /> {filterCounts.accIncomplete}
                                        </Button>
                                    </Tooltip>
                                    {!record?.canFinishAssistance ? (
                                        <Tooltip
                                            content={
                                                !record?.canFinishAssistance && t('finishButton.finishDisabledNote')
                                            }
                                            side="top"
                                            long
                                            className="flex flex-1"
                                        >
                                            <Button
                                                variant="primary"
                                                className="flex-1"
                                                disabled={loading || !record?.canFinishAssistance}
                                            >
                                                {t('finishButton.finishDocument')}
                                            </Button>
                                        </Tooltip>
                                    ) : (
                                        <Button
                                            variant="primary"
                                            className="flex-1"
                                            disabled={
                                                loading || checkingIncompletePositions || !record?.canFinishAssistance
                                            }
                                            // undefined (instead of false) keeps the button's own
                                            // promise-based loading active while the finish mutation runs
                                            loading={checkingIncompletePositions || undefined}
                                            onClick={handleFinishClick}
                                        >
                                            {t('finishButton.finishDocument')}
                                        </Button>
                                    )}
                                </div>
                                <ConfirmModal
                                    visible={skipPositionsModalVisible}
                                    onConfirm={() =>
                                        // returning the promise keeps the confirm button disabled/loading
                                        // until the finish has completed, preventing a double submit
                                        handlers.onFinish().finally(() => setSkipPositionsModalVisible(false))
                                    }
                                    onCancel={() => setSkipPositionsModalVisible(false)}
                                    labelConfirm={t('finishButton.skipAndFinish')}
                                    labelCancel={t('finishButton.cancel')}
                                    danger
                                >
                                    <div className="text-sm text-content-strong">
                                        <p>{t('finishButton.skipPositionsIntro')}</p>
                                        <p className="font-bold my-2">{incompleteRelevantPositionNumbers.join(', ')}</p>
                                        <p>{t('finishButton.skipPositionsConsequence')}</p>
                                    </div>
                                </ConfirmModal>
                            </div>
                        )}
                        <div
                            className={classnames(
                                'absolute inset-0 z-40 bg-surface-white border-l border-line-weak shadow-lg overflow-auto transition-transform duration-300 ease-in-out',
                                isSidebarOpen ? 'translate-x-0' : 'translate-x-full'
                            )}
                        >
                            {sidebarContent}
                        </div>
                    </Pane>
                </SplitPanes>
            </Pane>
        </SplitPanes>
    );

    return (
        <FocusPositionProvider
            focusArticles={focusArticles}
            suppressAutoSync={suppressAutoSync}
            autoScrollEnabled={autoScrollEnabled}
            setAutoScrollEnabled={setAutoScrollEnabled}
        >
            {main}
        </FocusPositionProvider>
    );
};

interface IFieldData {
    value?: string;
    predictionConfidence?: number;
    confidenceExplanation?: {
        translationKey: string;
        explanationDetails?: {
            confidence: number;
            field_value: string;
        };
    };
    choices?: string[]; // list of options comes from extracted data
    // ... and more but not sure if it's needed here
    [key: string]: any;
}

export interface IArticle {
    number: IFieldData;
    description: IFieldData;
    quantity: IFieldData;
    comments: IFieldData;
    suggestions?: ISuggestion[];
    unit: IFieldData;
}

interface IArticleSet {
    id: string;
    articles: IArticle[];
    isComplete: boolean;
}

interface IChapterProps {
    chapter: string;
    pageIndex: number;
    positions: IPosition[];
    isExpanded: boolean;
    numberTotal: number;
    numberRelevant: number;
    numberCompleted: number;
    containsHighlightKeywords: boolean;
    header: string;
    relevanceSelection: RelevanceSelectionState[];
    onChapterHeaderClick: (args: { openChapter: boolean; chapter: string }) => void;
    onMarkAllRelevantClick: (args: { chapter: string; subchapter?: string }) => void;
    onMarkAllNotRelevantClick: (args: { chapter: string; subchapter?: string }) => void;
    isChapterActionLoading: boolean;
    loadingChapter: boolean;
    allChaptersLoaded: boolean;
    onPositionRelevantChange: (args: { position: IPosition; isRelevant: boolean }) => void;
    onArticleSetIsCompleteChange: (args: { articleSet: IArticleSet; isComplete: boolean }) => void;
    onPositionIncludedChange: (args: { position: IPosition; isIncluded: boolean }) => void;
    isLoading?: boolean;
    applyToChapters: boolean;
    positionExpandFunctionsRef?: React.RefObject<Map<number, () => void>>;
    chapterIndex: number;
    isLastChapter: boolean;
    nextChapterKey: string | null;
    positionsScrollRef: React.RefObject<HTMLDivElement>;
}

export const getSubChapters = (positions: IPosition[]) => {
    const pretexts = positions.filter((p) => p.prediction === 'PreText');

    if (pretexts.length === 0) {
        return positions.length > 0 ? [positions] : [];
    }
    const byParent = new Map<string, IPosition[]>(pretexts.map((p) => [String(p.positionIndex), [p]]));
    const orphans: IPosition[] = [];

    positions.forEach((position) => {
        if (position.prediction === 'PreText') return;
        const parentKey = String(position.parent);
        if (byParent.has(parentKey)) {
            byParent.get(parentKey)!.push(position);
        } else {
            orphans.push(position);
        }
    });

    const result: IPosition[][] = [];
    if (orphans.length > 0) result.push(orphans);
    pretexts.forEach((p) => {
        const group = byParent.get(String(p.positionIndex));
        if (group) result.push(group);
    });
    return result;
};

const NoPositionsCard = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => {
    const { t } = useTranslation('assistance');

    const { relevanceSelection, setRelevanceSelection } = useListOfServicesAssistanceContext();

    return (
        <div className={classnames('sticky -top-6 w-3/5 flex-none', className)} {...props}>
            <div
                className={classnames(
                    'flex flex-col rounded-lg border border-line-weak border-solid bg-surface-strong'
                )}
            >
                <div className="p-6 flex flex-col gap-4">
                    <div className="text-center text-sm text-content-strong">
                        {t('listOfServices.noPositions.description')}
                    </div>
                    {relevanceSelection.length < 4 && (
                        <div className="flex justify-center">
                            <button
                                className="!text-content-strong rounded flex items-center px-3 py-1.5 font-medium text-sm border border-solid outline-none hover:bg-surface-strong transition-all bg-surface-weak border-line-strong shadow-sm"
                                onClick={() => setRelevanceSelection(ALL_RELEVANCE_SELECTION)}
                            >
                                {t('listOfServices.noPositions.buttonLabel')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const NoChaptersCard = () => {
    const { t } = useTranslation('assistance');

    const { setApplyToChapters } = useListOfServicesAssistanceContext();

    return (
        <div className="flex justify-center px-12 pt-6">
            <div className="w-3/5">
                <div
                    className={classnames(
                        'flex flex-col rounded-lg border border-line-weak border-solid bg-surface-strong'
                    )}
                >
                    <div className="p-6 flex flex-col gap-4">
                        <div className="text-center text-sm text-content-strong">
                            {t('listOfServices.noChapters.description')}
                        </div>
                        <div className="flex justify-center">
                            <button
                                className="!text-content-strong rounded flex items-center px-3 py-1.5 font-medium text-sm border border-solid outline-none hover:bg-surface-strong transition-all bg-surface-weak border-line-strong shadow-sm"
                                onClick={() => setApplyToChapters(false)}
                            >
                                {t('listOfServices.noChapters.disableFilterButton')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ChapterControls = ({
    className,
    numberCompleted,
    numberRelevant,
    numberTotal,
    isChapterActionLoading,
    hideRelevantSummary,
    onMarkAllRelevantClick,
    onMarkAllNotRelevantClick,
    articlesViewMode,
    onArticlesViewModeChange,
}: {
    className?: string;
    numberCompleted: number;
    numberRelevant: number;
    numberTotal: number;
    isChapterActionLoading: boolean;
    hideRelevantSummary?: boolean;
    onMarkAllRelevantClick?: () => void;
    onMarkAllNotRelevantClick?: () => void;
    articlesViewMode?: string;
    onArticlesViewModeChange?: (mode: string) => void;
}) => {
    const { t } = useTranslation('assistance');
    const { mode } = useListOfServicesAssistanceContext();

    return (
        <div className="flex gap-4 items-center -my-1">
            {articlesViewMode && onArticlesViewModeChange && (
                <>
                    <div className="flex gap-1">
                        <Tooltip content={t('itemsView.actions.listView')} side="top">
                            <button
                                className={classnames(
                                    'w-8 h-8 flex items-center justify-center rounded hover:bg-surface-weak transition-colors',
                                    articlesViewMode === 'list' &&
                                        '!bg-surface-brand-medium-default text-content-brand-strong'
                                )}
                                onClick={() => onArticlesViewModeChange('list')}
                            >
                                <ListViewIcon />
                            </button>
                        </Tooltip>

                        <Tooltip content={t('itemsView.actions.tableView')} side="top">
                            <button
                                className={classnames(
                                    'w-8 h-8 flex items-center justify-center rounded hover:bg-surface-weak transition-colors',
                                    articlesViewMode === 'table' &&
                                        '!bg-surface-brand-medium-default text-content-brand-strong'
                                )}
                                onClick={() => onArticlesViewModeChange('table')}
                            >
                                <TableViewIcon />
                            </button>
                        </Tooltip>
                    </div>
                </>
            )}

            {(onMarkAllNotRelevantClick || onMarkAllRelevantClick) && (
                <>
                    <div className="ml-auto">
                        <DropdownMenu>
                            <DropdownMenu.Trigger asChild>
                                <Button
                                    variant="ghost"
                                    className="w-8 h-8 flex items-center justify-center text-lg"
                                    loading={isChapterActionLoading}
                                >
                                    {isChapterActionLoading ? (
                                        <LoadingIcon spin className="text-content-brand-strong" />
                                    ) : (
                                        <MoreIcon />
                                    )}
                                </Button>
                            </DropdownMenu.Trigger>
                            {(onMarkAllRelevantClick || onMarkAllNotRelevantClick) && (
                                <DropdownMenu.Content align="end">
                                    {onMarkAllRelevantClick && (
                                        <DropdownMenu.Item
                                            onClick={() => onMarkAllRelevantClick()}
                                            className="flex gap-2 items-center"
                                        >
                                            <ThumbsUpIcon />
                                            {t('listOfServicesView.chapterMoreMenu.markAllRelevant')}
                                        </DropdownMenu.Item>
                                    )}
                                    {onMarkAllNotRelevantClick && (
                                        <DropdownMenu.Item
                                            onClick={() => onMarkAllNotRelevantClick()}
                                            className="flex gap-2 items-center"
                                        >
                                            <ThumbsDownIcon />
                                            {t('listOfServicesView.chapterMoreMenu.markAllNotRelevant')}
                                        </DropdownMenu.Item>
                                    )}
                                </DropdownMenu.Content>
                            )}
                        </DropdownMenu>
                    </div>
                </>
            )}
        </div>
    );
};

const scrollToAnchor = (positionsScrollRef: React.RefObject<HTMLDivElement>, nextAnchor: HTMLElement) => {
    const containerRect = positionsScrollRef.current.getBoundingClientRect();
    const anchorRect = nextAnchor.getBoundingClientRect();
    positionsScrollRef.current.scrollTo({
        top: anchorRect.top + positionsScrollRef.current.scrollTop - containerRect.top - SCROLL_ANCHOR_OFFSET,
        behavior: 'smooth',
    });
};

const ListOfServicesChapter = ({
    chapter,
    pageIndex,
    positions,
    numberTotal,
    numberRelevant,
    numberCompleted,
    containsHighlightKeywords,
    header,
    relevanceSelection,
    onChapterHeaderClick,
    onMarkAllRelevantClick,
    onMarkAllNotRelevantClick,
    onPositionRelevantChange,
    onArticleSetIsCompleteChange,
    onPositionIncludedChange,
    isChapterActionLoading,
    loadingChapter,
    allChaptersLoaded,
    isExpanded,
    isLoading,
    applyToChapters,
    positionExpandFunctionsRef,
    chapterIndex,
    isLastChapter,
    nextChapterKey,
    positionsScrollRef,
}: IChapterProps) => {
    const { t } = useTranslation('assistance');
    const { pendingPositionIndex, focusTrigger } = useFocusPosition();

    const handleScrollToNextChapter = () => {
        if (!positionsScrollRef?.current || !nextChapterKey) return;
        onChapterHeaderClick({ openChapter: true, chapter: nextChapterKey });
        setTimeout(() => {
            if (!positionsScrollRef?.current) return;
            const anchors = Array.from(
                positionsScrollRef.current.querySelectorAll('[data-scroll-anchor="chapterHeader"]')
            );
            const nextAnchor = anchors[chapterIndex + 1] as HTMLElement;
            if (nextAnchor) {
                scrollToAnchor(positionsScrollRef, nextAnchor);
            }
        }, 350);
    };

    const sentinelRef = useRef<HTMLDivElement>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    useEffect(() => {
        if (isLastChapter || !sentinelRef.current || !positionsScrollRef?.current) {
            setShowScrollButton(false);
            return;
        }
        const observer = new IntersectionObserver(([entry]) => setShowScrollButton(entry.isIntersecting), {
            root: positionsScrollRef.current,
            threshold: 0,
        });
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [isLastChapter, positionsScrollRef, isExpanded]);

    const getFloatingButtonStyle = (): React.CSSProperties => {
        if (!positionsScrollRef?.current)
            return { position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)' };
        const rect = positionsScrollRef.current.getBoundingClientRect();
        return { position: 'fixed', bottom: 32, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
    };

    const [forceDisplayPosition, setForceDisplayPosition] = useState<IPosition | null>(null);

    // Track sub-chapter expanded states so we can programmatically open them on focus
    const [subChapterExpandedMap, setSubChapterExpandedMap] = useState<Map<number, boolean>>(new Map());

    const highlightKeywords = useHighlightKeywords();

    const subChapters = useMemo(() => {
        // Add indexInChapter to each position needed to build a valid patch update in the backend
        positions.forEach((position, index) => {
            position.indexInChapter = index;
        });

        // Get subchapters based on pretexts
        const allSubChapters = getSubChapters(positions);

        // Filter subchapters if applyToChapters is enabled
        if (applyToChapters) {
            return allSubChapters.filter((subChapterPositions) => {
                // Always include pretext-only subchapters (no positions, just text)
                const pretext = subChapterPositions[0];
                const hasOnlyPretext = pretext.prediction === 'PreText' && subChapterPositions.length === 1;

                if (hasOnlyPretext) return true;

                // Check if this subchapter has any positions that match the filter
                return positionsMatchFilter(subChapterPositions, relevanceSelection);
            });
        }

        return allSubChapters;
    }, [positions, applyToChapters, relevanceSelection]);

    const hasSubChapters = subChapters.length > 1; // first one is the actual chapter

    // During render, if a pending focus targets a position in a sub-chapter, record that
    // sub-chapter as expanded in the map. React supports setState during render for derived
    // state — the guard prevents infinite re-render loops.
    if (pendingPositionIndex != null) {
        for (let i = 0; i < subChapters.length; i++) {
            if (subChapters[i].some((p) => p.positionIndex === pendingPositionIndex)) {
                if (subChapterExpandedMap.get(i) !== true) {
                    setSubChapterExpandedMap((prev) => {
                        const next = new Map(prev);
                        next.set(i, true);
                        return next;
                    });
                }
                break;
            }
        }
    }

    const chapterPageIndex = pageIndex ? pageIndex : positions.length > 0 ? positions[0].pageIndex : undefined;

    return (
        <Chapter
            data-scroll-anchor="chapterHeader"
            expanded={isExpanded}
            onExpandedChange={(expanded) =>
                onChapterHeaderClick({
                    openChapter: expanded,
                    chapter: chapter,
                })
            }
        >
            <Chapter.Header
                className="!z-30"
                pageIndex={chapterPageIndex}
                controls={
                    (!isExpanded || !hasSubChapters) && (
                        <ChapterControls
                            numberCompleted={numberCompleted}
                            numberRelevant={numberRelevant}
                            numberTotal={numberTotal}
                            isChapterActionLoading={isChapterActionLoading}
                            onMarkAllRelevantClick={() => onMarkAllRelevantClick({ chapter })}
                            onMarkAllNotRelevantClick={() => onMarkAllNotRelevantClick({ chapter })}
                        />
                    )
                }
                containsHighlightKeywords={containsHighlightKeywords}
                highlightKeywords={highlightKeywords}
                highlightText={
                    positions.length > 0
                        ? positions.reduce((acc, position) => acc + (position.text?.value || '') + ' ', '')
                        : ''
                }
            >
                {!header && t('listOfServicesView.chapter')} {chapter} {header}
                {loadingChapter && <LoadingIcon spin className="text-content-brand-strong ml-3" />}
                {isLoading && !loadingChapter && <LoadingIcon spin className="text-content-brand-strong ml-3" />}
            </Chapter.Header>
            <Chapter.Content>
                {subChapters.map((subChapterPositions, subChapterIndex) => {
                    const pretext = subChapterPositions[0];

                    const isRelevantOpen = (position: IPosition) =>
                        position.isRelevant.value === 'True' &&
                        position.articleSets.some((articleSet) => !articleSet.isComplete);
                    const isRelevantCompleted = (position: IPosition) =>
                        position.isRelevant.value === 'True' &&
                        position.articleSets.every((articleSet) => articleSet.isComplete);
                    const isIrrelevantOpen = (position: IPosition) =>
                        position.isRelevant.value === 'False' && position.feedbackRequest === 'need_feedback';
                    const isIrrelevantCompleted = (position: IPosition) =>
                        position.isRelevant.value === 'False' && position.feedbackRequest === 'no_feedback_needed';

                    const filteredSubChapterPositions = subChapterPositions.filter((position) => {
                        if (position.prediction === 'PreText') return true;
                        if (position === forceDisplayPosition) return true;

                        if (
                            isRelevantOpen(position) &&
                            relevanceSelection.includes(RelevanceSelectionState.RELEVANT_OPEN)
                        )
                            return true;
                        if (
                            isRelevantCompleted(position) &&
                            relevanceSelection.includes(RelevanceSelectionState.RELEVANT_COMPLETED)
                        )
                            return true;
                        if (
                            isIrrelevantOpen(position) &&
                            relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_OPEN)
                        )
                            return true;
                        if (
                            isIrrelevantCompleted(position) &&
                            relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_COMPLETED)
                        )
                            return true;
                        return false;
                    });

                    const regularDisplayPositions = filteredSubChapterPositions.filter(
                        (position) => position.prediction !== 'PreText'
                    );

                    const regularPositions = subChapterPositions.filter(
                        (position) => position.prediction !== 'PreText'
                    );
                    const subChapterNumberCompleted = regularPositions.filter(
                        (position) =>
                            position.isRelevant.value === 'True' &&
                            position.articleSets.every((articleSet) => articleSet.isComplete)
                    ).length;
                    const subChapterNumberRelevant = regularPositions.filter(
                        (position) => position.isRelevant.value === 'True'
                    ).length;
                    const subChapterNumberTotal = regularPositions.length;

                    const hasPositions = regularPositions.length > 0;
                    const hasTitle = Boolean(pretext.header);
                    const hasText = Boolean(pretext.text?.value);

                    const canExpand = hasPositions || hasText;
                    const showPageNumber = hasPositions || hasTitle || hasText;

                    // Check if this subchapter has matching positions
                    const hasMatchingPositions = positionsMatchFilter(subChapterPositions, relevanceSelection);

                    const subChapterExpanded = subChapterExpandedMap.get(subChapterIndex);

                    return (
                        <Chapter
                            key={`${chapter}-${subChapterIndex}`}
                            canExpand={canExpand}
                            {...(subChapterExpanded !== undefined
                                ? {
                                      expanded: subChapterExpanded,
                                      onExpandedChange: (expanded: boolean) => {
                                          setSubChapterExpandedMap((prev) => {
                                              const next = new Map(prev);
                                              next.set(subChapterIndex, expanded);
                                              return next;
                                          });
                                      },
                                  }
                                : { defaultExpanded: hasMatchingPositions })}
                            data-position-index={pretext.positionIndex}
                        >
                            {subChapterIndex > 0 && (
                                <Chapter.Header
                                    pageIndex={
                                        showPageNumber
                                            ? subChapterPositions.length > 0
                                                ? subChapterPositions[0].pageIndex
                                                : chapterPageIndex
                                            : undefined
                                    }
                                    controls={
                                        hasPositions && (
                                            <ChapterControls
                                                numberCompleted={subChapterNumberCompleted}
                                                numberRelevant={subChapterNumberRelevant}
                                                numberTotal={subChapterNumberTotal}
                                                isChapterActionLoading={isChapterActionLoading}
                                                onMarkAllRelevantClick={() =>
                                                    onMarkAllRelevantClick({
                                                        chapter,
                                                        subchapter: pretext.number?.value,
                                                    })
                                                }
                                                onMarkAllNotRelevantClick={() =>
                                                    onMarkAllNotRelevantClick({
                                                        chapter,
                                                        subchapter: pretext.number?.value,
                                                    })
                                                }
                                            />
                                        )
                                    }
                                    highlightKeywords={highlightKeywords}
                                    highlightText={filteredSubChapterPositions?.reduce(
                                        (acc, position) => acc + (position.text?.value || '') + ' ',
                                        ''
                                    )}
                                >
                                    {pretext.number?.value} {pretext.header}
                                </Chapter.Header>
                            )}

                            <Chapter.Content>
                                {filteredSubChapterPositions.map((position, i) => {
                                    return (
                                        <Fragment key={i}>
                                            {position.prediction === 'PreText' ? (
                                                position.text?.value ? (
                                                    <div className="w-full px-12 pl-[6rem] py-2 pb-3">
                                                        <Pretext
                                                            text={position.text?.value}
                                                            highlightKeywords={highlightKeywords}
                                                        />
                                                    </div>
                                                ) : null
                                            ) : hasPositions ? (
                                                <ListOfServicesPositionContainer
                                                    key={position.positionIndex}
                                                    setForceDisplayPosition={setForceDisplayPosition}
                                                    position={position}
                                                    onRelevantChange={(isRelevant) => {
                                                        return onPositionRelevantChange({ position, isRelevant });
                                                    }}
                                                    onIsCompleteChange={(articleSet, isComplete) => {
                                                        return onArticleSetIsCompleteChange({ articleSet, isComplete });
                                                    }}
                                                    onIncludedChange={(isIncluded) => {
                                                        return onPositionIncludedChange({ position, isIncluded });
                                                    }}
                                                    positionExpandFunctionsRef={positionExpandFunctionsRef}
                                                    positionsScrollRef={positionsScrollRef}
                                                />
                                            ) : null}
                                        </Fragment>
                                    );
                                })}
                                {hasPositions &&
                                    regularDisplayPositions.length === 0 &&
                                    (subChapterIndex !== 0 || !hasSubChapters) && (
                                        <NoPositionsCard className="w-3/5 pl-12 pr-2 py-4" />
                                    )}
                            </Chapter.Content>
                        </Chapter>
                    );
                })}
                {!isLastChapter && <div ref={sentinelRef} aria-hidden="true" />}
                {showScrollButton && (
                    <button
                        style={getFloatingButtonStyle()}
                        onClick={handleScrollToNextChapter}
                        className="z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-surface-black shadow-lg text-sm font-medium text-content-white hover:opacity-90 transition-opacity cursor-pointer"
                    >
                        {t('listOfServicesView.scrollToNextChapter')}
                    </button>
                )}
            </Chapter.Content>
        </Chapter>
    );
};

interface ListOfServicesPositionContainerProps {
    position: IPosition;
    setForceDisplayPosition: (ForceDisplayPosition: IPosition | null) => void;
    onRelevantChange: (isRelevant: boolean) => void;
    onIsCompleteChange: (articleSet: IArticleSet, isComplete: boolean) => void;
    onIncludedChange: (isIncluded: boolean) => void;
    positionExpandFunctionsRef?: React.RefObject<Map<number, () => void>>;
    positionsScrollRef?: React.RefObject<HTMLDivElement>;
}

const ListOfServicesPositionContainer = ({
    position,
    setForceDisplayPosition,
    onRelevantChange,
    onIsCompleteChange,
    onIncludedChange,
    positionExpandFunctionsRef,
    positionsScrollRef,
}: ListOfServicesPositionContainerProps) => {
    const { handlers, readOnly, record, loading } = useAssistanceContext();
    const { t } = useTranslation('assistance');
    const { mode, relevanceSelection } = useListOfServicesAssistanceContext();
    const { pendingPositionIndex, focusTrigger, consumeFocus, focusArticles, suppressAutoSync } = useFocusPosition();
    const highlightKeywords = useHighlightKeywords();

    const highlightKeywordCounts = countKeywordMatches(position.text?.value || '', highlightKeywords);
    const hasHighlightKeywords = Object.keys(highlightKeywordCounts).length > 0;

    const { publishToast } = useToaster();

    const isRelevant = position.isRelevant.value === 'True';
    const isFeedbackNeeded = position.feedbackRequest === 'need_feedback';
    const isComplete = isRelevant && position.articleSets.every((articleSet) => articleSet.isComplete);
    // the explicit setting wins; inferring from article presence is the fallback for
    // legacy positions saved before isIncluded was stored
    const isIncluded = isRelevant && (position.isIncluded ?? Boolean(position.articleSets.length > 0));

    const [expanded, setExpanded] = useState(!isComplete && isRelevant && isIncluded);
    const [manuallyCollapsed, setManuallyCollapsed] = useState(false);
    useEffect(() => {
        if (!manuallyCollapsed) {
            setExpanded((!isComplete && isRelevant && isIncluded) || hasHighlightKeywords);
        }
    }, [isComplete, isRelevant, isIncluded, hasHighlightKeywords, manuallyCollapsed]);

    useEffect(() => {
        if (positionExpandFunctionsRef?.current) {
            positionExpandFunctionsRef.current.set(position.positionIndex, () => setExpanded(true));
            return () => {
                positionExpandFunctionsRef.current?.delete(position.positionIndex);
            };
        }
    }, [position.positionIndex, positionExpandFunctionsRef]);

    // Handle pending focus: expand, reset manual collapse, and scroll into view
    useEffect(() => {
        if (pendingPositionIndex !== position.positionIndex) return;

        consumeFocus();
        setExpanded(true);
        setManuallyCollapsed(false);

        // The smooth scroll below fires scroll events that the left→right auto-follow would
        // treat as user scrolling and move the articles column (ENG-10485)
        suppressAutoSync(2000);

        // Scroll into view after the DOM has updated
        requestAnimationFrame(() => {
            if (positionsScrollRef?.current) {
                scrollToPositionElement(positionsScrollRef.current, position.positionIndex, 'smooth');
            }
        });
    }, [
        position.positionIndex,
        pendingPositionIndex,
        focusTrigger,
        consumeFocus,
        suppressAutoSync,
        positionsScrollRef,
    ]);

    const handleIncludedChange = useCallback(
        (isIncluded: boolean) => {
            if (!isIncluded) {
                setExpanded(false);
                setManuallyCollapsed(true);
            } else {
                setManuallyCollapsed(false);
            }

            return handlers
                .onUpdate({
                    itemIndex: position.positionIndex,
                    action: 'action:change_included',
                    payload: {
                        isIncluded: isIncluded,
                        indexInChapter: position.indexInChapter,
                    },
                })
                .then(() => {
                    onIncludedChange(isIncluded);
                });
        },
        [handlers, position.positionIndex, position.indexInChapter, onIncludedChange]
    );

    const [isExitAnimationActive, setIsExitAnimationActive] = useState(false);

    const title = position.text?.value?.split('\n')[0];

    return (
        <div
            className={classnames(
                'grid transition-all duration-300 ease-in-out origin-top',
                isExitAnimationActive ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
            )}
            data-position-index={position.positionIndex}
        >
            <div className="w-full px-12 flex justify-between items-center min-w-0 min-h-0">
                <div className="flex justify-between w-3/5 items-center py-2 min-w-0 flex-none">
                    <div
                        className={classnames(
                            'rounded w-8 h-8 ml-2 flex items-center justify-center transition-opacity opacity-100 flex-none',
                            isRelevant === true && 'bg-surface-success-weak text-content-success-strong',
                            isRelevant === false && 'bg-surface-danger-weak text-content-danger-strong',
                            isFeedbackNeeded && 'bg-surface-warning-weak text-content-warning-strong',
                            expanded && 'opacity-0'
                        )}
                    >
                        {isRelevant === true ? (
                            <ThumbsUpIcon />
                        ) : isRelevant === false ? (
                            <ThumbsDownIcon />
                        ) : (
                            <Loader />
                        )}
                    </div>
                    <div
                        className="font-semibold text-content-strong text-sm w-fit cursor-pointer flex items-center min-w-0 mr-2"
                        onClick={() => {
                            setManuallyCollapsed(expanded);
                            setExpanded(!expanded);
                        }}
                    >
                        <div className="w-10 text-center flex-none">
                            {expanded ? (
                                <ChevronUpIcon className="text-content-strong text-sm" />
                            ) : (
                                <ChevronDownIcon className="text-content-strong text-sm" />
                            )}
                        </div>
                        {/* Only included positions have an article group in the right pane to scroll to;
                            the placeholder keeps position numbers aligned across rows */}
                        {isIncluded ? (
                            <FocusButton onClick={() => focusArticles(position.positionIndex)} />
                        ) : (
                            <div className="w-8 flex-none" />
                        )}
                        <div className="truncate min-w-0 flex-1">
                            {position.number.value} {title}
                        </div>
                    </div>

                    <a
                        className="font-medium text-sm ml-auto text-content-brand-default hover:text-content-brand-hover flex-none"
                        href={`${record?.inputFile?.url}#page=${Number(position.pageIndex) + 1}`}
                        target="_blank"
                    >
                        {t('listOfServicesView.page')} {Number(position.pageIndex) + 1}
                        <ExternalLinkIcon className={classnames('ml-2')} />
                    </a>
                </div>

                {!expanded && isComplete && (
                    <div>
                        <CompleteButton isComplete={true} />
                    </div>
                )}
            </div>

            <div
                className={classnames(
                    'grid transition-all px-12 duration-300 ease-in-out origin-top',
                    expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
            >
                <div
                    className={classnames(
                        'relative flex justify-end items-start gap-4 mb-6 px-12 min-h-0 transition-all duration-300',
                        isExitAnimationActive && 'mb-0',
                        expanded ? 'pb-2 opacity-100' : 'mb-0 overflow-hidden opacity-0'
                    )}
                >
                    <PositionAssistanceCard
                        number={position.number.value}
                        description={<HighlightText text={position.text?.value} keywords={highlightKeywords} />}
                        isRelevant={isRelevant}
                        isFeedbackNeeded={isFeedbackNeeded}
                        reason={position.rejectedBy?.value}
                        articlesCount={position.articleSets.length}
                        className={classnames('sticky top-16', 'w-full flex-none')}
                        onRelevantChange={(isRelevant) => {
                            // On relevancy change, show irrelevant position for a short time even when filter is active
                            setForceDisplayPosition?.(position);

                            if (!isRelevant) {
                                setExpanded(false);
                            }

                            const shouldShowIrrelevant =
                                relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_OPEN) ||
                                relevanceSelection.includes(RelevanceSelectionState.IRRELEVANT_COMPLETED);

                            if (!isRelevant && !shouldShowIrrelevant) {
                                setTimeout(() => {
                                    setIsExitAnimationActive(true);
                                }, 600);

                                publishToast({
                                    title: t('listOfServices.positionHiddenToast.title'),
                                    description: t('listOfServices.positionHiddenToast.description'),
                                });
                            }
                            return handlers
                                .onUpdate({
                                    action: 'action:change_relevancy',
                                    itemIndex: position.positionIndex,
                                    payload: { isRelevant: isRelevant, indexInChapter: position.indexInChapter },
                                })
                                .then(async () => {
                                    // call onRelevantChange to update the UI after the update is complete
                                    await onRelevantChange(isRelevant);

                                    setTimeout(() => {
                                        setForceDisplayPosition?.(null);
                                        setIsExitAnimationActive(false);
                                    }, 2000);
                                });
                        }}
                        pageIndex={Number(position.pageIndex) + 1}
                        isIncluded={isIncluded}
                        onIncludedChange={handleIncludedChange}
                    />
                </div>
            </div>
        </div>
    );
};

export const ListOfServicesHeaderDataContainer = (props) => {
    const { handlers, record } = useAssistanceContext();

    const documentConfig = record?.channel?.documentConfig;

    return (
        <div className={classnames('relative flex justify-end items-start gap-4')}>
            <div className="w-full sticky top-2 flex flex-col gap-8 items-center">
                <HeaderDataCard
                    className="w-full"
                    values={record?.listOfServicesUnsaved}
                    onValueChange={(fieldName, payload) => {
                        return handlers.onUpdate({
                            action: 'action:change_header_field',
                            fieldName: fieldName,
                            payload: payload,
                        });
                    }}
                    isComplete={record?.isHeaderCorrect}
                    onIsCompleteChange={(isComplete) => {
                        return handlers.onUpdate({ action: 'action:mark_header_correct' });
                    }}
                    documentConfig={documentConfig}
                />
            </div>
        </div>
    );
};

const SummaryRow = ({
    number,
    description,
    quantity,
    unit,
}: {
    number: string;
    description: string;
    quantity: string;
    unit: string;
}) => {
    return (
        <div className="flex gap-10 items-center">
            <div className="w-40">{number}</div>
            <div className="flex-1">{description}</div>
            <div className="w-20 text-right">{quantity}</div>
            <div className="w-20">{unit}</div>
        </div>
    );
};

const RELEVANCE_PARAMS_KEY = 'relevanceSelection';
const RELEVANCE_LOCAL_STORAGE_KEY = 'list-of-services-assistance-relevance-selection';
const DEFAULT_RELEVANCE_SELECTION = [
    RelevanceSelectionState.RELEVANT_OPEN,
    RelevanceSelectionState.RELEVANT_COMPLETED,
    RelevanceSelectionState.IRRELEVANT_OPEN,
    RelevanceSelectionState.IRRELEVANT_COMPLETED,
];
const ALL_RELEVANCE_SELECTION = [
    RelevanceSelectionState.RELEVANT_OPEN,
    RelevanceSelectionState.RELEVANT_COMPLETED,
    RelevanceSelectionState.IRRELEVANT_OPEN,
    RelevanceSelectionState.IRRELEVANT_COMPLETED,
];

const APPLY_TO_CHAPTERS_PARAM_KEY = 'applyToChapters';
const APPLY_TO_CHAPTERS_LOCAL_STORAGE_KEY = 'list-of-services-assistance-apply-to-chapters';

const SHOW_ONLY_INCOMPLETE_PARAM_KEY = 'showOnlyIncomplete';
const SHOW_ONLY_INCOMPLETE_LOCAL_STORAGE_KEY = 'list-of-services-assistance-show-only-incomplete';

const MemoAssistanceHeader = React.memo((props) => {
    return <ListOfServicesAssistanceHeader {...props} />;
});

const ListOfServicesAssistance = (props) => {
    const [mode, setMode] = useState<'EDITOR' | 'SUMMARY'>('EDITOR');
    const [storedRelevanceSelection, setStoredRelevanceSelection] = useLocalStorage(
        RELEVANCE_LOCAL_STORAGE_KEY,
        DEFAULT_RELEVANCE_SELECTION
    );
    const [relevanceSelection, _setRelevanceSelection] = useArraySearchParamState(
        RELEVANCE_PARAMS_KEY,
        storedRelevanceSelection,
        'NO_SELECTION'
    );
    const setRelevanceSelection = (newSelection: RelevanceSelectionState[]) => {
        _setRelevanceSelection(newSelection.map((selection) => selection.toString()));
        setStoredRelevanceSelection(newSelection);
    };

    const mappedRelevanceSelection = useMemo(() => {
        return relevanceSelection.map((selection) => {
            return RelevanceSelectionState[selection as keyof typeof RelevanceSelectionState];
        });
    }, [relevanceSelection]);

    const [scrollAnchorIndex, setScrollAnchorIndex] = useState(0);
    const [storedApplyToChapters, setStoredApplyToChapters] = useLocalStorage(
        APPLY_TO_CHAPTERS_LOCAL_STORAGE_KEY,
        false
    );
    const [applyToChapters, _setApplyToChapters] = useBooleanSearchParamState(
        APPLY_TO_CHAPTERS_PARAM_KEY,
        storedApplyToChapters
    );
    const setApplyToChapters = (newValue: boolean) => {
        _setApplyToChapters(newValue);
        setStoredApplyToChapters(newValue);
    };

    const [storedShowOnlyIncomplete, setStoredShowOnlyIncomplete] = useLocalStorage(
        SHOW_ONLY_INCOMPLETE_LOCAL_STORAGE_KEY,
        false
    );
    const [showOnlyIncomplete, _setShowOnlyIncomplete] = useBooleanSearchParamState(
        SHOW_ONLY_INCOMPLETE_PARAM_KEY,
        storedShowOnlyIncomplete
    );
    const setShowOnlyIncomplete = (newValue: boolean) => {
        _setShowOnlyIncomplete(newValue);
        setStoredShowOnlyIncomplete(newValue);
    };

    const documentConfiguration = useMemo(
        () => ({
            documentType: DocumentType.ListOfServices,
            documentTypeName: 'listOfServices',
            GET_ASSISTANCE_OVERVIEW_DATA: GET_ASSISTANCE_OVERVIEW_DATA,
            GET_CHANNELS_OVERVIEW_DATA: GET_CHANNELS_OVERVIEW_DATA,
            GET_NEXT_ASSISTANCE_RECORD: GET_NEXT_ASSISTANCE_RECORD,
            GET_RELATED_DOCUMENT: GET_RELATED_DOCUMENT,
            RETRY_STEP: RETRY_STEP,
            REOPEN_FOR_ASSISTANCE: REOPEN_FOR_ASSISTANCE,
            ASSISTANCE_PATH: ASSISTANCE_PATH,
            ASSISTANCE_TAB_PATH: ASSISTANCE_TAB_PATH,
            OVERVIEW_PATH: OVERVIEW_PATH,
            OVERVIEW_FINISHED_PATH: OVERVIEW_FINISHED_PATH,
            OVERVIEW_TESTING_PATH: OVERVIEW_TESTING_PATH,
            CHANNEL_PATH: CHANNEL_PATH,
            CHANNEL_FINISHED_PATH: CHANNEL_FINISHED_PATH,
            CHANNEL_TESTING_PATH: CHANNEL_TESTING_PATH,
            RE_UPLOAD_FILE: RE_UPLOAD_FILE,
            SEND_TO_LABELING: SEND_TO_LABELING,
            components: {
                Field: [
                    ...ArticleNumberFieldConditionalComponents,
                    // make them explicit for list of services to not use the "confirm" variant
                    {
                        condition: ({ fieldName, itemIndex }) =>
                            itemIndex === undefined && fieldName === `customer__customer_number`,
                        component: CustomerNumberField,
                    },
                    {
                        condition: ({ fieldName }) => fieldName === `customer__name`,
                        component: CustomerNumberField, // We can use the same component for both fields
                    },
                    ...getCustomerFieldConditionalComponents('customer'),
                ],
                HeaderDataFieldGroup: [...getCustomerFieldGroupConditionalComponents('customer')],
            },
            AssistanceHeader: MemoAssistanceHeader,
            tabs: {
                [TABS_OPTIONS.Source]: ({ record, loading, readOnly }) => {
                    const sourceFile = record?.sourceFile;
                    return <SourceFileView file={sourceFile} readOnly={readOnly} loading={loading} />;
                },
                [TABS_OPTIONS.Document]: Positions,
            },
            firstItemsTab: TABS_OPTIONS.Document,
            defaultTab: TABS_OPTIONS.Document,
        }),
        [mode, relevanceSelection]
    );

    const [articlesViewMode, setArticlesViewMode] = useLocalStorage('assistanceItemsViewMode', 'table');

    return (
        <ListOfServicesStateProvider>
            <ListOfServicesAssistanceContext.Provider
                value={{
                    mode,
                    setMode,
                    scrollAnchorIndex,
                    setScrollAnchorIndex,
                    relevanceSelection: mappedRelevanceSelection,
                    setRelevanceSelection,
                    applyToChapters,
                    setApplyToChapters,
                    showOnlyIncomplete,
                    setShowOnlyIncomplete,
                    articlesViewMode,
                    setArticlesViewMode,
                }}
            >
                <DocumentAssistanceWithContext documentConfiguration={documentConfiguration} {...props} />
            </ListOfServicesAssistanceContext.Provider>
        </ListOfServicesStateProvider>
    );
};

const DocumentAssistanceWithContext = (props) => {
    const { applyPositionPatch } = useListOfServicesState();
    return <DocumentAssistance onDocumentPatched={(doc) => applyPositionPatch(doc?.positions)} {...props} />;
};

interface IAssistanceContext {
    mode: 'EDITOR' | 'SUMMARY';
    setMode: (mode: 'EDITOR' | 'SUMMARY') => void;
    relevanceSelection: RelevanceSelectionState[];
    setRelevanceSelection: (relevanceSelection: RelevanceSelectionState[]) => void;
    scrollAnchorIndex: number;
    setScrollAnchorIndex: (scrollAnchorIndex: number) => void;
    articlesViewMode: string;
    setArticlesViewMode: (mode: string) => void;
    highlightKeywords?: string[];
    applyToChapters: boolean;
    setApplyToChapters: (applyToChapters: boolean) => void;
    showOnlyIncomplete: boolean;
    setShowOnlyIncomplete: (showOnlyIncomplete: boolean) => void;
}

const ListOfServicesAssistanceContext = React.createContext<IAssistanceContext>({} as IAssistanceContext);
export const useListOfServicesAssistanceContext = () => React.useContext(ListOfServicesAssistanceContext);

const Positions = React.memo((props: any) => {
    return <ListOfServicesPositionsTab {...props} key="positions" tab="positions" />;
});

export default ListOfServicesAssistance;
